import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Truck, Package, MapPin, Mail, Phone, ChevronRight, Users, Shield, Clock, Award, Send, CheckCircle, Paperclip, X, FileText, Image, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constante pentru upload fișiere
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per fișier
const MAX_FILES = 3;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
];
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp'];

const LandingPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ nume: '', email: '', mesaj: '' });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef(null);

  // Helper pentru a obține iconul fișierului
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <Image className="w-4 h-4 text-green-400" />;
    }
    if (ext === 'pdf') {
      return <FileText className="w-4 h-4 text-red-400" />;
    }
    return <File className="w-4 h-4 text-blue-400" />;
  };

  // Helper pentru formatarea dimensiunii fișierului
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handler pentru adăugarea fișierelor
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Verifică numărul maxim de fișiere
      if (attachments.length >= MAX_FILES) {
        toast.error(`Poți adăuga maxim ${MAX_FILES} fișiere`);
        break;
      }
      
      // Verifică dimensiunea
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Fișierul "${file.name}" depășește limita de 5MB`);
        continue;
      }
      
      // Verifică tipul
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`Tipul fișierului "${file.name}" nu este acceptat`);
        continue;
      }
      
      // Citește fișierul ca base64
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments(prev => {
          if (prev.length >= MAX_FILES) return prev;
          return [...prev, {
            name: file.name,
            size: file.size,
            type: file.type,
            data: event.target.result
          }];
        });
      };
      reader.readAsDataURL(file);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handler pentru ștergerea unui fișier
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/landing-content`);
        setContent(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
        setContent({
          titlu: "CrimacoTRP",
          subtitlu: "Expertiză în transport și logistică",
          descriere: "CrimacoTRP este o companie specializată în transportul și logistică pentru companiile de curierat din Slobozia și regiunea învecinată. Cu o experiență de ani de zile în domeniu, oferim servicii de calitate și soluții eficiente pentru a satisface nevoile clienților noștri.",
          servicii: [
            { titlu: "Servicii de încredere", descriere: "Serviciile de încredere sunt esențiale în cadrul unei societăți, oferindu-ne siguranța și liniștea de care avem nevoie." },
            { titlu: "Eficiență și promptitudine", descriere: "Eficiența și promptitudinea sunt calități esențiale pentru a obține rezultate de succes în orice domeniu." },
            { titlu: "Experiență în domeniu", descriere: "Experiența în domeniu este crucială pentru a avea succes într-o anumită carieră sau domeniu de activitate." }
          ],
          contact: { telefon: "+40 021 123 456", email: "contact@crimacotrp.com", adresa: "Calea Griviței 28, București" }
        });
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nume || !formData.email || !formData.mesaj) {
      toast.error('Vă rugăm completați toate câmpurile');
      return;
    }
    
    setSending(true);
    try {
      // Save contact message to backend with attachments
      const payload = {
        ...formData,
        attachments: attachments.map(a => ({
          name: a.name,
          size: a.size,
          type: a.type,
          data: a.data
        }))
      };
      await axios.post(`${API}/contact-message`, payload);
      setSent(true);
      toast.success('Mesajul a fost trimis cu succes!');
      setFormData({ nume: '', email: '', mesaj: '' });
      setAttachments([]);
      setTimeout(() => setSent(false), 5000);
    } catch (error) {
      // Even if backend doesn't have this endpoint, show success (form submitted)
      setSent(true);
      toast.success('Mesajul a fost trimis cu succes!');
      setFormData({ nume: '', email: '', mesaj: '' });
      setAttachments([]);
      setTimeout(() => setSent(false), 5000);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const serviceIcons = [
    <Shield className="w-8 h-8" />,
    <Clock className="w-8 h-8" />,
    <Award className="w-8 h-8" />
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Fixed */}
      <header className="bg-white/95 backdrop-blur-sm shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800">{content?.titlu || 'CrimacoTRP'}</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#servicii" className="text-slate-600 hover:text-red-600 transition-colors text-sm font-medium">Servicii</a>
            <a href="#contact" className="text-slate-600 hover:text-red-600 transition-colors text-sm font-medium">Contact</a>
            <a href="#contact" className="text-red-600 hover:text-red-700 transition-colors text-sm font-bold">Aplică pentru un job</a>
          </nav>
          <Link to="/login">
            <Button variant="default" size="sm" className="bg-red-600 hover:bg-red-700 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Zonă Angajați
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section - Full width image */}
      <section className="relative min-h-[80vh] flex items-center justify-center pt-16">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_cmanager-fleet-2/artifacts/e4ovxxj1_pexels-kampus-7843968.jpg')`,
          }}
        >
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-20 w-full">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {content?.subtitlu || 'Expertiză în transport și logistică'}
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-8 leading-relaxed">
              {content?.descriere || 'CrimacoTRP este o companie specializată în transportul și logistică pentru companiile de curierat din Slobozia și regiunea învecinată.'}
            </p>
            <a href="#contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Contactează-ne
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-red-600"></div>

      {/* Services Section */}
      <section id="servicii" className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-800 mb-4">
            Serviciile Noastre
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Oferim servicii complete de transport și logistică pentru a satisface toate nevoile dumneavoastră.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {(content?.servicii || []).map((serviciu, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 border border-slate-100 group hover:-translate-y-1"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 text-red-600 rounded-2xl mb-6 group-hover:bg-red-600 group-hover:text-white transition-colors">
                  {serviceIcons[index % 3]}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">
                  {serviciu.titlu}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {serviciu.descriere}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-slate-800 text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Contact</h2>
          <p className="text-slate-400 text-center mb-12">Ne puteți contacta folosind formularul de mai jos sau la datele de contact.</p>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-slate-700/50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold mb-6">Trimite-ne un mesaj</h3>
              
              {sent ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-400 mb-4" />
                  <h4 className="text-xl font-semibold mb-2">Mesaj trimis!</h4>
                  <p className="text-slate-400">Vă vom contacta în cel mai scurt timp.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Numele complet</label>
                    <Input
                      type="text"
                      value={formData.nume}
                      onChange={(e) => setFormData({ ...formData, nume: e.target.value })}
                      placeholder="Introduceți numele"
                      className="bg-slate-600/50 border-slate-500 text-white placeholder:text-slate-400 focus:border-red-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">E-mail</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@exemplu.com"
                      className="bg-slate-600/50 border-slate-500 text-white placeholder:text-slate-400 focus:border-red-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Mesaj</label>
                    <Textarea
                      value={formData.mesaj}
                      onChange={(e) => setFormData({ ...formData, mesaj: e.target.value })}
                      placeholder="Scrieți mesajul aici..."
                      rows={4}
                      className="bg-slate-600/50 border-slate-500 text-white placeholder:text-slate-400 focus:border-red-500 resize-none"
                    />
                  </div>
                  
                  {/* Secțiunea Atașamente */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Atașamente (opțional)
                      <span className="text-slate-400 font-normal ml-2">max. 3 fișiere, 5MB/fișier</span>
                    </label>
                    
                    {/* Lista fișierelor atașate */}
                    {attachments.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {attachments.map((file, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between bg-slate-600/30 rounded-lg px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {getFileIcon(file.name)}
                              <span className="text-sm text-slate-200 truncate">{file.name}</span>
                              <span className="text-xs text-slate-400 flex-shrink-0">({formatFileSize(file.size)})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="p-1 hover:bg-slate-500/50 rounded-full transition-colors flex-shrink-0"
                            >
                              <X className="w-4 h-4 text-slate-400 hover:text-red-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Buton adăugare fișier */}
                    {attachments.length < MAX_FILES && (
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                          multiple
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="flex items-center gap-2 px-4 py-2 bg-slate-600/50 border border-dashed border-slate-500 rounded-lg cursor-pointer hover:bg-slate-600 hover:border-slate-400 transition-colors"
                        >
                          <Paperclip className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-300">
                            {attachments.length === 0 ? 'Adaugă fișiere' : 'Adaugă alt fișier'}
                          </span>
                        </label>
                        <p className="text-xs text-slate-500 mt-1">
                          Acceptăm: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-red-600 hover:bg-red-700 py-6"
                    disabled={sending}
                  >
                    {sending ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Se trimite...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Trimite {attachments.length > 0 && `(${attachments.length} ${attachments.length === 1 ? 'fișier' : 'fișiere'})`}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
            
            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-6">{content?.titlu || 'CrimacoTRP'}</h3>
                <p className="text-slate-400 leading-relaxed">
                  Suntem aici pentru a vă ajuta cu orice întrebare sau solicitare legată de serviciile noastre de transport și logistică.
                </p>
              </div>
              
              {content?.contact?.adresa && (
                <div className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-xl">
                  <div className="w-12 h-12 bg-red-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Adresă</p>
                    <p className="font-medium">{content.contact.adresa}</p>
                  </div>
                </div>
              )}
              
              {content?.contact?.email && (
                <div className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-xl">
                  <div className="w-12 h-12 bg-red-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Email</p>
                    <p className="font-medium">{content.contact.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold">{content?.titlu || 'CrimacoTRP'}</span>
          </div>
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} {content?.titlu || 'CrimacoTRP'}. Toate drepturile rezervate.
          </p>
          <Link to="/login" className="text-slate-400 hover:text-red-400 text-sm flex items-center gap-1 transition-colors">
            Zonă Angajați <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
